import React from 'react';
import { Link } from 'react-router-dom';
import jucoLogo from '../../assets/images/Logo_JUCO_header.png';

// Navy footer shown under every page that uses the main navbar.
// Links only point at pages that exist; add new ones here as pages go live.
const LINK_GROUPS = [
  {
    title: 'Explore',
    links: [
      { name: 'Home', to: '/' },
      { name: 'For Players', to: '/for-players' },
      { name: 'For Coaches', to: '/for-coaches' },
      { name: 'Pricing', to: '/pricing' },
    ],
  },
  {
    title: 'Company',
    links: [
      { name: 'About Us', to: '/about' },
      { name: 'Support', to: '/support' },
      { name: 'Contact', href: 'mailto:support@jucopipeline.com' },
    ],
  },
  {
    title: 'Account',
    links: [
      { name: 'Log In', to: '/auth/login' },
      { name: 'Sign Up', to: '/auth/signup' },
    ],
  },
];

// Fill in each profile URL to show its icon; icons without a URL stay hidden.
const SOCIAL_LINKS = [
  {
    label: 'Instagram',
    url: 'https://www.instagram.com/jucopipeline/',
    icon: (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <rect x="3" y="3" width="18" height="18" rx="5" />
        <circle cx="12" cy="12" r="4" />
        <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
      </svg>
    ),
  },
  {
    label: 'X',
    url: 'https://x.com/JucoPipeline',
    icon: (
      <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.45-6.231zm-1.161 17.52h1.833L7.084 4.126H5.117l11.966 15.644z" />
      </svg>
    ),
  },
];

const LINK_CLASS =
  'text-slate-300 text-sm hover:text-cyan-400 transition-colors rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400';

const FooterLink = ({ link }) =>
  link.to ? (
    <Link to={link.to} className={LINK_CLASS}>
      {link.name}
    </Link>
  ) : (
    <a href={link.href} className={LINK_CLASS}>
      {link.name}
    </a>
  );

const Footer = () => {
  const socials = SOCIAL_LINKS.filter((social) => social.url);

  return (
    <footer className="text-left bg-jp-band border-t-2 border-jp-gold/60 px-4 sm:px-6 lg:px-8 pt-12 pb-8">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-[2fr_1fr_1fr_1fr] gap-8 mb-10">
          {/* Brand */}
          <div className="col-span-2 sm:col-span-3 lg:col-span-1">
            <Link to="/" className="inline-block rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400">
              <img src={jucoLogo} alt="JUCO Pipeline home" className="h-14 w-auto" />
            </Link>
            <p className="text-slate-300 text-sm leading-relaxed mt-4 max-w-sm">
              Connecting talented JUCO baseball players with scouts, coaches, and teams. Your pathway to the next
              level starts here.
            </p>
            {socials.length > 0 && (
              <div className="flex items-center gap-3 mt-6">
                <p className="text-white text-base font-bold">
                  Follow <span className="text-cyan-400">@jucopipeline</span>
                </p>
                {socials.map((social) => (
                  <a
                    key={social.label}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`JUCO Pipeline on ${social.label}`}
                    className="w-10 h-10 rounded-full bg-cyan-400/10 border border-cyan-400/60 text-cyan-400 hover:bg-cyan-400 hover:text-jp-navy flex items-center justify-center transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400"
                  >
                    {social.icon}
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Link columns */}
          {LINK_GROUPS.map((group) => (
            <nav key={group.title} aria-label={group.title}>
              <h2 className="text-yellow-300 text-xs font-bold uppercase tracking-widest mb-4">{group.title}</h2>
              <ul className="space-y-2.5">
                {group.links.map((link) => (
                  <li key={link.name}>
                    <FooterLink link={link} />
                  </li>
                ))}
              </ul>
            </nav>
          ))}
        </div>

        {/* Bottom bar */}
        <div className="border-t border-white/10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-400">
          <p>© {new Date().getFullYear()} JUCO Pipeline. All rights reserved.</p>
          <Link to="/terms" className="hover:text-cyan-400 transition-colors">
            Terms of Service
          </Link>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
